import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CognitiveFilterRequestModel,
  CognitiveSearchRequestModel,
  CognitiveSearchResultModel,
  ModuleBaseModel,
  ModuleBasePropsModel,
  ReportSearchModel,
} from '@/types';
import {
  AppInsightsContext,
  MetadataContext,
  ModuleBase,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import SearchTermInput from '@/components/search/SearchTermInput';
import { searchReports } from '@/api';
import { useRouter } from 'next/router';
import {
  assignValuesFromRouteQuery,
  getSearchFilters,
  pushValuesToRouteQuery,
  replaceValuesOfRouteQuery,
} from '@/utils';
import { ReportsSearchFilterType } from '@/components/search/SearchFilters';
import SearchFilterOptions from '@/components/search/SearchFilterOptions';
import ReportCarouselHeading from '@/components/report/ReportCarouselHeading';
import ReportCard from '@/components/report/ReportCard';
import { useTranslations } from 'next-intl';

type GlobalDataIntegration0Model = ModuleBaseModel<
  {
    availableFilters: ReportsSearchFilterType[];
    requestFilter?: CognitiveFilterRequestModel;
  } & ModuleBasePropsModel
>;

const GlobalDataIntegration0: React.FC<GlobalDataIntegration0Model> = (data) => {
  const {
    props: { availableFilters, requestFilter },
  } = data;
  const pageSize = 12;

  const getSkeletons = (numberOfSkeletons: number) => {
    const skeletons: ReportSearchModel[] = [];
    for (let index = 0; index < numberOfSkeletons; index++) {
      skeletons.push({ reportId: index, isLoading: true } as ReportSearchModel);
    }
    return skeletons;
  };

  const [result, setResult] = useState<CognitiveSearchResultModel<ReportSearchModel>>({
    facets: [],
    results: getSkeletons(pageSize),
    totalResults: 0,
  });

  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const currentCardPage = useRef<number>(0);

  const { pageId } = useContext(MetadataContext);
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const router = useRouter();
  const t = useTranslations();

  useEffect(() => {
    if (router.isReady) {
      const searchTermFromQuery = { searchTerm: '' };
      assignValuesFromRouteQuery(router, searchTermFromQuery);
      setSearchTerm(searchTermFromQuery.searchTerm || '');
      setResult((currentValue) => ({ facets: currentValue.facets, results: getSkeletons(pageSize), totalResults: 0 }));
      currentCardPage.current = 0;
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  const getData = () => {
    const nameMap: { [key: string]: string } = {};

    availableFilters.forEach((filter) => {
      nameMap[filter.key] = filter.name;
    });

    const filters = getSearchFilters(router, nameMap);

    const filterRequest: CognitiveSearchRequestModel = {
      ...requestFilter,
      facetsMode: 'IncludeFacets',
      searchTerm: Array.isArray(router.query.searchTerm) ? router.query.searchTerm[0] : router.query.searchTerm || '*',
      filters: [...(requestFilter?.filters || []), ...filters],
      pageSize,
      page: currentCardPage.current,
      orderBy: 'score',
      account: user?.account,
      userId: user?.userId,
    };
    searchReports(filterRequest).then((dataResponse) => {
      setResult((result) => {
        return {
          ...dataResponse,
          results: [...result.results.filter((item) => !item.isLoading), ...dataResponse.results],
        };
      });
    });
  };
  const handleSubmit = () => {
    pushValuesToRouteQuery(router, { searchTerm: searchTerm || null });
    currentCardPage.current = 0;
  };

  const handleClear = () => {
    setSearchTerm('');
    replaceValuesOfRouteQuery(router, {});
    currentCardPage.current = 0;
  };

  const onCardClick = (e: React.MouseEvent, data: ReportSearchModel) => {
    if (data.isGdReport) return;
    appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.REPORTS_GRID, TrackEventType.SELECTED, user, {
      pageId,
      pagePath: router.asPath,
      reportID: data.reportId,
    });
  };

  const viewMoreLink = useMemo(() => {
    const queryParams = new URLSearchParams({
      SiteId: '272306',
    } as Record<string, string>);

    if (searchTerm) {
      queryParams.append('searchTerm', searchTerm);
    }

    availableFilters.forEach((filter) => {
      const valueFromQuery = router.query[filter.key];
      if (valueFromQuery) {
        if (Array.isArray(valueFromQuery)) {
          valueFromQuery.forEach((value) => {
            queryParams.append(filter.key, value);
          });
        } else {
          queryParams.append(filter.key, valueFromQuery);
        }
      }
    });

    return `/search?${queryParams.toString()}`;
  }, [router.query, searchTerm, availableFilters]);

  return (
    <ModuleBase data={data} noPadding className="overflow-hidden">
      <div className="flex w-full justify-center">
        <div className="w-full">
          <div className="pb-6 lg:pb-10 xl:pb-16">
            <div id="global-data-integration" className="container mx-auto px-4">
              <div className="rounded-full border border-[#c6c6c6] px-4 py-4">
                <SearchTermInput
                  placeholder="Search GlobalData reports"
                  autofocus={false}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onSubmit={handleSubmit}
                  onClear={handleClear}
                  className="[&_.btn]:mr-3 [&_.btn]:capitalize [&_svg]:fill-black/50"
                />
              </div>
            </div>
            <div className="container mx-auto flex flex-col justify-between p-4 md:flex-row">
              <SearchFilterOptions availableFilters={availableFilters} facets={result.facets} isPublic={false} />
              <div className="mt-2 whitespace-nowrap md:mt-0">
                {t('search.$searchtotalresults', {
                  totalResults: result?.totalResults || 0,
                })}
              </div>
            </div>
          </div>

          <div className="mx-auto bg-[#d8d8d8] pb-10">
            <div className="container">
              <ReportCarouselHeading
                heading={{
                  heading: 'GlobalData Analysis',
                  headingTag: 'h2',
                  link: {
                    href: viewMoreLink,
                  },
                }}
                hideArrows
                className="!pb-0"
              />

              {result.results.length === 0 && (
                <span className="pt-10 text-base text-gray-600">Sorry, nothing has been found</span>
              )}

              <div className="-mx-3 mt-3 flex flex-wrap">
                {result.results.map((report) => (
                  <div key={report.reportId} className="xxl:w-3/12 w-full p-3 md:w-1/2 lg:w-4/12">
                    <ReportCard key={report.reportId} data={report} onClick={onCardClick} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default GlobalDataIntegration0;
